import React, { FC, useCallback, useMemo, useState } from 'react';
import styled, { StyledComponent } from 'styled-components';
import classNames from 'classnames';
import { Top } from '@sunbit-dev/purchase-common/src/components/top/Top';
import { SupportPopup } from '@sunbit-dev/purchase-common/src/components/supportPopup/SupportPopup';
import { PopupWrapper } from '@sunbit-dev/purchase-common/src/components/popup/Popup';
import * as styles from './styles';
import { ApplicationLayoutProps } from './models';
import { PrivacyAllowanceLayer } from '@sunbit-dev/purchase-common/src/components/privacyAllowanceLayer/PrivacyAllowanceLayer';
import { useApplicationInitializer } from './useApplicationInitializer';
import { Loader } from '@sunbit-dev/purchase-common/src/components/loader/Loader';
import { Stepper } from '@sunbit-dev/web-ui-lib';
import { useSteps } from '~/components/app/components/applicationLayout/useSteps';
import * as customerStore from '~/store/reducers/customer/customer';
import { LandingPage } from '../landingPage/LandingPage';
import { useDispatch, useSelector } from 'react-redux';
import { checkIsRoutePublic } from './utils';
import { useOnLogin } from '~/hooks/login';
import { EventsNames } from '~/enums/Events';
import { useTrack } from '~/hooks/track';
import { LoanApplicationOrigin } from '~/enums/LoanApplicationOrigin';
import messageService from '~/services/MessageService';
import * as purchaseStore from '~/store/reducers/purchase/purchase';
import * as introStore from '~/store/reducers/intro/intro';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService';
import { ReturnToCartPopup } from '@sunbit-dev/purchase-common/src/components/returnToCartPopup/ReturnToCartPopup';
import { addError } from '~/store/reducers/errors/errors';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import URLs, { isErrorExternalMessageRoute } from '~/enums/URLs';
import { isEmbeddedMode } from '@sunbit-dev/purchase-common/src/helpers/embeddedMode';
import { hrefForReferer } from '~/helpers/referer';

export const ApplicationLayout: StyledComponent<FC<ApplicationLayoutProps>, {}> = styled(({ className, children }) => {
  const { isInitializing } = useApplicationInitializer();
  const { steps, currentStepPosition, showStepper: showStepperByCurrentStep } = useSteps();
  const [isSupportPopupOpen, setIsSupportPopupOpen] = useState(false);
  const [isReturnPopupOpen, setIsReturnPopupOpen] = useState(false);
  const { isLoggedIn } = useSelector(customerStore.getStore);
  const { initialRouteByStatusTriggered } = useOnLogin();
  const { track } = useTrack();
  const { pathname, search } = useLocation();
  const isExternalErrorPage = isErrorExternalMessageRoute(qs.parse(search, { ignoreQueryPrefix: true }).token);
  const dispatch = useDispatch();
  const { offer, purchaseInfo, referer } = useSelector(purchaseStore.getStore);
  const { merchant } = useSelector(introStore.getStore);

  const isInEmbeddedMode = isEmbeddedMode();

  const toggleSupportPopup = useCallback(() => {
    setIsSupportPopupOpen((state) => !state);
  }, []);

  const isPublicRoute = checkIsRoutePublic();

  // TODO: Remove all except ORGANIC_PREQUAL_LINK after phase 2 of the prequal deprecation
  const prequalOriginsWithReferer = [
    LoanApplicationOrigin.ORGANIC_PREQUAL_LINK,
    LoanApplicationOrigin.MERCHANT_PREQUAL_LINK,
    LoanApplicationOrigin.ALLIANCE_PREQUAL_LINK,
    LoanApplicationOrigin.CALL_CENTER_PREQUAL_LINK,
  ];

  const onReturnToCart = useCallback(async () => {
    setIsReturnPopupOpen(false);
    if (pathname !== URLs.Thanks) {
      track({ eventName: EventsNames.customerPurchaseCancellation });
      if (offer) {
        try {
          await loanApplicationNetworkService.cancelPurchase({ offerId: offer.offerId.toString() });
        } catch (error) {
          dispatch(addError({ eventName: EventsNames.customerPurchaseCancellation, payload: error }));
        }
      }
      if (purchaseInfo?.loanApplicationOrigin === LoanApplicationOrigin.ONLINE_SDK) {
        messageService.messageUserCancel(offer?.offerId.toString());
      }
      if (purchaseInfo?.loanApplicationOrigin === LoanApplicationOrigin.ALLIANCE_LINK && purchaseInfo?.redirectUrl)
        window.location.href = purchaseInfo.redirectUrl;
      if (prequalOriginsWithReferer.includes(purchaseInfo?.loanApplicationOrigin) && referer) {
        window.location.href = hrefForReferer(referer, purchaseInfo?.loanApplicationOrigin);
      }
    }
  }, [purchaseInfo, offer, referer]);

  const shouldShowCloseButton = useMemo(() => {
    return (
      purchaseInfo?.loanApplicationOrigin === LoanApplicationOrigin.ONLINE_SDK ||
      (purchaseInfo?.loanApplicationOrigin === LoanApplicationOrigin.ALLIANCE_LINK && purchaseInfo?.redirectUrl) ||
      (prequalOriginsWithReferer.includes(purchaseInfo?.loanApplicationOrigin) && referer)
    );
  }, [purchaseInfo, referer]);

  const content = useMemo(() => {
    if (isInitializing || (isLoggedIn && !initialRouteByStatusTriggered && !isExternalErrorPage)) {
      return <Loader contained />;
    } else if (isExternalErrorPage) {
      return null;
    } else if (isPublicRoute) {
      return children;
    } else if (!isLoggedIn) {
      return <LandingPage />;
    }

    return children;
  }, [isInitializing, children, isLoggedIn, isPublicRoute, initialRouteByStatusTriggered, isExternalErrorPage]);

  const showStepper = isLoggedIn && showStepperByCurrentStep;

  const merchantName = purchaseInfo?.merchantName ?? merchant?.merchantName;

  return (
    <div className={classNames(className, 'application-layout')}>
      <div className="content">
        <PrivacyAllowanceLayer>
          {isReturnPopupOpen && (
            <PopupWrapper>
              <ReturnToCartPopup onConfirm={onReturnToCart} onClose={() => setIsReturnPopupOpen(false)} noLightBox />
            </PopupWrapper>
          )}
          {isSupportPopupOpen && (
            <PopupWrapper>
              <SupportPopup onClose={toggleSupportPopup} noLightBox hideCallOption={isInEmbeddedMode} />
            </PopupWrapper>
          )}

          <Top
            className={classNames('top', { 'no-shadow': showStepper })}
            showCloseButton={shouldShowCloseButton}
            headerReturnClick={() => setIsReturnPopupOpen(true)}
            onSupportClick={toggleSupportPopup}
            merchantName={merchantName}
          />
          {showStepper && <Stepper className="stepper" steps={steps} currentStepPosition={currentStepPosition} />}
        </PrivacyAllowanceLayer>
        {content}
      </div>
    </div>
  );
})`
  ${styles.ApplicationLayout}
`;
